import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Timer, Gift, AlertTriangle, Calendar, Clock } from 'lucide-react'
import { getTimeRemainingInEligibilityWindow } from '../../types/loyalty'

interface RewardEligibilityTimerProps {
  registrationDate: Date
  variant?: 'compact' | 'full' | 'banner'
  className?: string
  showIcon?: boolean
}

const RewardEligibilityTimer = ({
  registrationDate,
  variant = 'full',
  className = '',
  showIcon = true
}: RewardEligibilityTimerProps) => {
  const [timeRemaining, setTimeRemaining] = useState(() =>
    getTimeRemainingInEligibilityWindow(registrationDate)
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(getTimeRemainingInEligibilityWindow(registrationDate))
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [registrationDate])

  // Don't show anything if expired
  if (timeRemaining.expired) {
    return null
  }

  // Determine urgency level
  const getUrgencyLevel = () => {
    if (timeRemaining.days <= 7) return 'critical'
    if (timeRemaining.days <= 30) return 'warning'
    return 'normal'
  }

  const urgencyLevel = getUrgencyLevel()

  // Color schemes based on urgency
  const getColorScheme = () => {
    switch (urgencyLevel) {
      case 'critical':
        return {
          bg: 'from-red-500 to-orange-500',
          border: 'border-red-300',
          text: 'text-red-900',
          lightBg: 'from-red-50 to-orange-50',
          accent: 'text-red-600'
        }
      case 'warning':
        return {
          bg: 'from-orange-500 to-yellow-500',
          border: 'border-orange-300',
          text: 'text-orange-900',
          lightBg: 'from-orange-50 to-yellow-50',
          accent: 'text-orange-600'
        }
      default:
        return {
          bg: 'from-green-500 to-emerald-500',
          border: 'border-green-300',
          text: 'text-green-900',
          lightBg: 'from-green-50 to-emerald-50',
          accent: 'text-green-600'
        }
    }
  }

  const colors = getColorScheme()

  // Format time display
  const formatTimeDisplay = () => {
    if (timeRemaining.days > 0) {
      return {
        primary: timeRemaining.days,
        primaryLabel: timeRemaining.days === 1 ? 'día' : 'días',
        secondary: timeRemaining.hours,
        secondaryLabel: 'h'
      }
    } else if (timeRemaining.hours > 0) {
      return {
        primary: timeRemaining.hours,
        primaryLabel: timeRemaining.hours === 1 ? 'hora' : 'horas',
        secondary: timeRemaining.minutes,
        secondaryLabel: 'm'
      }
    } else {
      return {
        primary: timeRemaining.minutes,
        primaryLabel: timeRemaining.minutes === 1 ? 'minuto' : 'minutos',
        secondary: null,
        secondaryLabel: ''
      }
    }
  }

  const timeDisplay = formatTimeDisplay()

  // Compact variant
  if (variant === 'compact') {
    return (
      <motion.div
        className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-gradient-to-r ${colors.bg} text-white text-sm font-semibold ${className}`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.05 }}
      >
        {showIcon && <Timer className="w-4 h-4" />}
        <span>
          {timeDisplay.primary} {timeDisplay.primaryLabel}
          {timeDisplay.secondary && ` ${timeDisplay.secondary}${timeDisplay.secondaryLabel}`}
        </span>
      </motion.div>
    )
  }

  // Banner variant
  if (variant === 'banner') {
    return (
      <motion.div
        className={`w-full p-4 rounded-xl bg-gradient-to-r ${colors.lightBg} border ${colors.border} ${className}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {showIcon && (
              <div className={`p-2 rounded-full bg-gradient-to-r ${colors.bg} text-white`}>
                {urgencyLevel === 'critical' ? (
                  <AlertTriangle className="w-5 h-5" />
                ) : (
                  <Gift className="w-5 h-5" />
                )}
              </div>
            )}
            <div>
              <h4 className={`font-bold ${colors.text}`}>
                {urgencyLevel === 'critical'
                  ? '¡Tiempo crítico!'
                  : urgencyLevel === 'warning'
                  ? '¡Tiempo limitado!'
                  : 'Ventana de recompensas activa'
                }
              </h4>
              <p className={`text-sm ${colors.accent}`}>
                {urgencyLevel === 'critical'
                  ? 'Solo te quedan unos días para conseguir recompensas físicas'
                  : 'Completa logros ahora para recibir recompensas físicas'
                }
              </p>
            </div>
          </div>

          <div className="text-right">
            <div className={`text-2xl font-bold ${colors.text}`}>
              {timeDisplay.primary}
            </div>
            <div className={`text-sm ${colors.accent}`}>
              {timeDisplay.primaryLabel}
              {timeDisplay.secondary && (
                <span className="ml-2 text-lg font-semibold">
                  {timeDisplay.secondary}{timeDisplay.secondaryLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  // Full variant (default)
  return (
    <motion.div
      className={`p-6 rounded-xl bg-gradient-to-br ${colors.lightBg} border ${colors.border} ${className}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="text-center space-y-4">
        {/* Icon and Title */}
        <div className="flex items-center justify-center space-x-3">
          {showIcon && (
            <motion.div
              className={`p-3 rounded-full bg-gradient-to-r ${colors.bg} text-white`}
              animate={urgencyLevel === 'critical' ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 1, repeat: Infinity }}
            >
              {urgencyLevel === 'critical' ? (
                <AlertTriangle className="w-6 h-6" />
              ) : (
                <Gift className="w-6 h-6" />
              )}
            </motion.div>
          )}
          <h3 className={`text-xl font-bold ${colors.text}`}>
            {urgencyLevel === 'critical'
              ? '¡Último momento!'
              : urgencyLevel === 'warning'
              ? 'Tiempo limitado'
              : 'Ventana de elegibilidad activa'
            }
          </h3>
        </div>

        {/* Countdown Display */}
        <div className="flex items-center justify-center space-x-6">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className={`text-4xl font-bold ${colors.text}`}>
              {timeDisplay.primary}
            </div>
            <div className={`text-sm font-medium ${colors.accent} uppercase tracking-wider`}>
              {timeDisplay.primaryLabel}
            </div>
          </motion.div>

          {timeDisplay.secondary && (
            <>
              <div className={`text-2xl font-bold ${colors.text}`}>:</div>
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className={`text-4xl font-bold ${colors.text}`}>
                  {timeDisplay.secondary}
                </div>
                <div className={`text-sm font-medium ${colors.accent} uppercase tracking-wider`}>
                  {timeDisplay.secondaryLabel}
                </div>
              </motion.div>
            </>
          )}
        </div>

        {/* Description */}
        <div className={`text-sm ${colors.accent} max-w-md mx-auto`}>
          {urgencyLevel === 'critical' ? (
            <p>
              ⚠️ <strong>Solo te quedan {timeDisplay.primary} {timeDisplay.primaryLabel}</strong> para conseguir
              recompensas físicas. Después de este tiempo, solo recibirás marcos visuales.
            </p>
          ) : urgencyLevel === 'warning' ? (
            <p>
              🎯 <strong>Tienes {timeDisplay.primary} {timeDisplay.primaryLabel}</strong> para completar logros
              y recibir recompensas físicas completas.
            </p>
          ) : (
            <p>
              🎁 <strong>Tienes {timeDisplay.primary} {timeDisplay.primaryLabel}</strong> desde tu registro
              para conseguir recompensas físicas al completar logros.
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <motion.div
            className={`h-2 rounded-full bg-gradient-to-r ${colors.bg}`}
            initial={{ width: 0 }}
            animate={{
              width: `${Math.max(0, Math.min(100, (365 - timeRemaining.days) / 365 * 100))}%`
            }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>

        {/* Call to Action */}
        <motion.div
          className={`text-xs ${colors.accent} font-medium`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {urgencyLevel === 'critical' ? (
            '💨 ¡Actúa rápido! Compra tickets para ganar puntos y desbloquear logros.'
          ) : (
            '🚀 Participa en sorteos para ganar puntos y desbloquear logros con recompensas.'
          )}
        </motion.div>
      </div>
    </motion.div>
  )
}

export default RewardEligibilityTimer